/**
 * LoginPage.jsx
 * ─────────────
 * Shared login / registration page at /admin/login.
 * Used by: admins, risk managers, and users requesting access.
 *
 * Screens:
 *   "login"       — tab 1: email + password + remember me + forgot password
 *   "register"    — tab 2: name + email + password → status: pending (awaits admin approval)
 *   "forgot"      — enter email → sends reset link
 *   "reset"       — arrives via ?reset_token= in URL
 *   "must_change" — forced after admin password reset
 */

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { login, register, forgotPassword, resetPasswordViaToken, changePassword } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const lbl = { fontSize: 13, fontWeight: 600, color: "#5C4B00" };

const inp = {
  width: "100%", padding: "9px 12px", fontSize: 14,
  border: "1.5px solid #d4dbc8", borderRadius: 8,
  outline: "none", fontFamily: "inherit", marginTop: 5,
  color: "#5C4B00", background: "#fff",
};

function PwdField({ label, value, onChange, name }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={lbl}>{label}</label>
      <input type="password" style={inp} value={value} onChange={onChange}
             required placeholder="••••••••" autoComplete={name} minLength={8} />
    </div>
  );
}

export default function LoginPage({ adminMode }) {
  const { loginSuccess } = useAuth();
  const navigate         = useNavigate();
  const [searchParams]   = useSearchParams();

  const [screen,      setScreen]      = useState("login");
  const [email,       setEmail]       = useState("");
  const [password,    setPassword]    = useState("");
  const [newPass,     setNewPass]     = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [rememberMe,  setRememberMe]  = useState(false);
  const [busy,        setBusy]        = useState(false);
  const [msg,         setMsg]         = useState({ text: "", ok: true });

  // Registration fields
  const [regName,    setRegName]    = useState("");
  const [regEmail,   setRegEmail]   = useState("");
  const [regPwd,     setRegPwd]     = useState("");
  const [regConfirm, setRegConfirm] = useState("");

  // Forced-change flow
  const [pendingToken,   setPendingToken]   = useState("");
  const [pendingUser,    setPendingUser]    = useState(null);
  const [pendingOldPass, setPendingOldPass] = useState("");
  const [urlResetToken,  setUrlResetToken]  = useState("");

  useEffect(() => {
    const tok = searchParams.get("reset_token");
    if (tok) { setUrlResetToken(tok); setScreen("reset"); }
  }, [searchParams]);

  function show(s) {
    setScreen(s);
    setMsg({ text: "", ok: true });
    setPassword(""); setNewPass(""); setConfirmPass("");
    setRegPwd(""); setRegConfirm("");
  }

  // ── Handlers ──────────────────────────────────────────────────────────────

  async function handleLogin(e) {
    e.preventDefault(); setBusy(true); setMsg({ text: "", ok: true });
    try {
      const data = await login(email, password);
      if (data.must_change_password) {
        setPendingToken(data.access_token);
        setPendingUser(data.user);
        setPendingOldPass(password);
        show("must_change");
        setMsg({ text: "Your password has been reset. Please set a new one to continue.", ok: true });
        return;
      }
      loginSuccess(data.access_token, data.user, rememberMe);
      navigate(
        data.user.role === "admin"        ? "/admin"   :
        data.user.role === "risk_manager" ? "/catalog" : "/"
      );
    } catch (err) { setMsg({ text: err.message, ok: false }); }
    finally { setBusy(false); }
  }

  async function handleRegister(e) {
    e.preventDefault();
    if (regName.trim().length < 2) { setMsg({ text: "Name must be at least 2 characters.", ok: false }); return; }
    if (regPwd.length < 8)         { setMsg({ text: "Password must be at least 8 characters.", ok: false }); return; }
    if (regPwd !== regConfirm)     { setMsg({ text: "Passwords do not match.", ok: false }); return; }
    setBusy(true); setMsg({ text: "", ok: true });
    try {
      const data = await register(regName.trim(), regEmail, regPwd);
      setMsg({ text: data.message, ok: true });
      setRegName(""); setRegEmail(""); setRegPwd(""); setRegConfirm("");
    } catch (err) { setMsg({ text: err.message, ok: false }); }
    finally { setBusy(false); }
  }

  async function handleForgot(e) {
    e.preventDefault(); setBusy(true); setMsg({ text: "", ok: true });
    try { const d = await forgotPassword(email); setMsg({ text: d.message, ok: true }); }
    catch (err) { setMsg({ text: err.message, ok: false }); }
    finally { setBusy(false); }
  }

  async function handleReset(e) {
    e.preventDefault();
    if (newPass !== confirmPass) { setMsg({ text: "Passwords do not match.", ok: false }); return; }
    if (newPass.length < 8)     { setMsg({ text: "Minimum 8 characters.", ok: false }); return; }
    setBusy(true); setMsg({ text: "", ok: true });
    try {
      const d = await resetPasswordViaToken(urlResetToken, newPass);
      setMsg({ text: d.message + " Redirecting…", ok: true });
      setTimeout(() => { navigate("/admin/login", { replace: true }); show("login"); }, 2500);
    } catch (err) { setMsg({ text: err.message, ok: false }); }
    finally { setBusy(false); }
  }

  async function handleMustChange(e) {
    e.preventDefault();
    if (newPass !== confirmPass) { setMsg({ text: "Passwords do not match.", ok: false }); return; }
    if (newPass.length < 8)     { setMsg({ text: "Minimum 8 characters.", ok: false }); return; }
    setBusy(true); setMsg({ text: "", ok: true });
    try {
      sessionStorage.setItem("token", pendingToken);
      await changePassword(pendingOldPass, newPass);
      loginSuccess(pendingToken, pendingUser, rememberMe);
      navigate(
        pendingUser.role === "admin"        ? "/admin"   :
        pendingUser.role === "risk_manager" ? "/catalog" : "/"
      );
    } catch (err) { sessionStorage.removeItem("token"); setMsg({ text: err.message, ok: false }); }
    finally { setBusy(false); }
  }

  // ── Shared styles ──────────────────────────────────────────────────────────

  const card = {
    background: "#fff", border: "1.5px solid #D4D980",
    borderRadius: 14, padding: "1.75rem",
    boxShadow: "0 4px 20px rgba(75,117,24,.1)",
  };

  const btnPrimary = {
    width: "100%", padding: "11px", fontSize: 14, fontWeight: 700,
    background: "#4B7518", color: "#fff", border: "none", borderRadius: 9,
    cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.7 : 1,
    fontFamily: "'Nunito', sans-serif", display: "flex",
    alignItems: "center", justifyContent: "center", gap: 8,
  };

  // ── Sub-components (Msg and Tabs only — PwdField is at module scope) ───────

  function Msg() {
    if (!msg.text) return null;
    return (
      <div style={{
        fontSize: 13, padding: "10px 14px", borderRadius: 8, marginBottom: 14,
        background: msg.ok ? "#EAF3DE" : "#FCEBEB",
        color:      msg.ok ? "#1e4a00" : "#A32D2D",
        border:     `1px solid ${msg.ok ? "#9BC071" : "#f0a0a0"}`,
        lineHeight: 1.55,
      }}>{msg.text}</div>
    );
  }

  // Tab bar — shown on login and register screens only
  function Tabs() {
    return (
      <div style={{
        display: "flex", marginBottom: 22,
        border: "1.5px solid #d4dbc8", borderRadius: 10, overflow: "hidden",
      }}>
        {[
          { s: "login",    label: "Sign in"  },
          { s: "register", label: "Register" },
        ].map(({ s, label }, i) => (
          <button
            key={s}
            type="button"
            onClick={() => show(s)}
            style={{
              flex: 1, padding: "9px 0", fontSize: 13, fontWeight: 700,
              background: screen === s ? "#4B7518" : "transparent",
              color:      screen === s ? "#fff"    : "#775F46",
              border: "none",
              borderLeft: i > 0 ? "1.5px solid #d4dbc8" : "none",
              cursor: "pointer", fontFamily: "inherit",
              transition: "background .2s, color .2s",
            }}
          >
            {label}
          </button>
        ))}
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{
      minHeight: "100vh", background: "#F6F9F2",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "2rem 1rem",
    }}>
      <div style={{ width: "100%", maxWidth: 420 }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <img src="/logo.png" alt="BeXyl" style={{ height: 70, marginBottom: 10 }}
               onError={e => e.target.style.display = "none"} />
        </div>

        {/* ── SIGN IN ── */}
        {screen === "login" && (
          <div style={card}>
            <Tabs />
            <Msg />
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Email</label>
                <input type="email" style={inp} value={email}
                       onChange={e => setEmail(e.target.value)}
                       required placeholder="you@example.com" autoComplete="email" />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={lbl}>Password</label>
                <input type="password" style={inp} value={password}
                       onChange={e => setPassword(e.target.value)}
                       required placeholder="••••••••" autoComplete="current-password" />
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                <label style={{ fontSize: 13, color: "#775F46", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6 }}>
                  <input type="checkbox" checked={rememberMe}
                         onChange={e => setRememberMe(e.target.checked)}
                         style={{ accentColor: "#4B7518" }} />
                  Remember me
                </label>
                <button type="button" onClick={() => show("forgot")} style={{
                  background: "none", border: "none", fontSize: 13,
                  color: "#4B7518", cursor: "pointer", padding: 0, fontWeight: 600,
                }}>
                  Forgot password?
                </button>
              </div>
              <button type="submit" style={btnPrimary} disabled={busy}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.5">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3"/>
                </svg>
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <a href="/" style={{ fontSize: 13, color: "#775F46" }}>← Back to public site</a>
            </div>
          </div>
        )}

        {/* ── REGISTER ── */}
        {screen === "register" && (
          <div style={card}>
            <Tabs />
            <Msg />
            <form onSubmit={handleRegister}>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Full name</label>
                <input type="text" style={inp} value={regName}
                       onChange={e => setRegName(e.target.value)}
                       required placeholder="Your name" minLength={2} autoComplete="name" />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Email</label>
                <input type="email" style={inp} value={regEmail}
                       onChange={e => setRegEmail(e.target.value)}
                       required placeholder="you@example.com" autoComplete="email" />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Password <span style={{ fontWeight: 400, color: "#aaa" }}>(min. 8 characters)</span></label>
                <input type="password" style={inp} value={regPwd}
                       onChange={e => setRegPwd(e.target.value)}
                       required placeholder="••••••••" minLength={8} autoComplete="new-password" />
              </div>
              <div style={{ marginBottom: 22 }}>
                <label style={lbl}>Confirm password</label>
                <input type="password" style={inp} value={regConfirm}
                       onChange={e => setRegConfirm(e.target.value)}
                       required placeholder="••••••••" minLength={8} autoComplete="new-password" />
              </div>
              <button type="submit" style={btnPrimary} disabled={busy}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <line x1="19" y1="8" x2="19" y2="14"/>
                  <line x1="22" y1="11" x2="16" y2="11"/>
                </svg>
                {busy ? "Submitting…" : "Request access"}
              </button>
            </form>
            <div style={{
              marginTop: 16, padding: "10px 14px", borderRadius: 8,
              background: "#f0f8e8", border: "1px solid #c6dfa0",
              fontSize: 12, color: "#3a5c12", lineHeight: 1.6,
            }}>
              <strong>How it works:</strong> After submitting, an administrator will review your request and activate your account. You will receive an email notification when approved.
            </div>
            <div style={{ textAlign: "center", marginTop: 14 }}>
              <a href="/" style={{ fontSize: 13, color: "#775F46" }}>← Back to public site</a>
            </div>
          </div>
        )}

        {/* ── FORGOT PASSWORD ── */}
        {screen === "forgot" && (
          <div style={card}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#5C4B00",
              fontFamily: "'Nunito',sans-serif", marginBottom: 12 }}>
              Reset password
            </div>
            <Msg />
            <form onSubmit={handleForgot}>
              <div style={{ marginBottom: 16 }}>
                <label style={lbl}>Your email</label>
                <input type="email" style={inp} value={email}
                       onChange={e => setEmail(e.target.value)}
                       required placeholder="you@example.com" />
              </div>
              <button type="submit" style={btnPrimary} disabled={busy}>
                {busy ? "Sending…" : "Send reset link"}
              </button>
            </form>
            <button onClick={() => show("login")} style={{
              background: "none", border: "none", fontSize: 13, color: "#775F46",
              cursor: "pointer", marginTop: 14, display: "block",
            }}>← Back to login</button>
          </div>
        )}

        {/* ── RESET VIA TOKEN ── */}
        {screen === "reset" && (
          <div style={card}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#5C4B00",
              fontFamily: "'Nunito',sans-serif", marginBottom: 12 }}>
              Set new password
            </div>
            <Msg />
            <form onSubmit={handleReset}>
              <PwdField label="New password" value={newPass}
                        onChange={e => setNewPass(e.target.value)} name="new-password" />
              <PwdField label="Confirm password" value={confirmPass}
                        onChange={e => setConfirmPass(e.target.value)} name="new-password" />
              <button type="submit" style={btnPrimary} disabled={busy}>
                {busy ? "Saving…" : "Set new password"}
              </button>
            </form>
          </div>
        )}

        {/* ── FORCED PASSWORD CHANGE ── */}
        {screen === "must_change" && (
          <div style={card}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#5C4B00",
              fontFamily: "'Nunito',sans-serif", marginBottom: 8 }}>
              Set a new password
            </div>
            <Msg />
            <form onSubmit={handleMustChange}>
              <PwdField label="New password (min. 8 characters)" value={newPass}
                        onChange={e => setNewPass(e.target.value)} name="new-password" />
              <PwdField label="Confirm new password" value={confirmPass}
                        onChange={e => setConfirmPass(e.target.value)} name="new-password" />
              <button type="submit" style={btnPrimary} disabled={busy}>
                {busy ? "Saving…" : "Set password and continue"}
              </button>
            </form>
          </div>
        )}

      </div>
    </div>
  );
}
